import { createHmac, randomBytes, timingSafeEqual } from "node:crypto";
import { base32Decode, base32Encode } from "./base32";

/**
 * RFC 6238 TOTP over RFC 4226 HOTP (X1 AUTH, design section X1): HMAC-SHA1,
 * 30 second step, 6 digits, plus or minus one step of drift, and replay refused
 * by the caller's stored `last_used_step`. Pure apart from `generateTotpSecret`
 * (the one place that draws randomness). Pinned to the RFC vectors in
 * totp.test.ts. The step, digit and drift numbers are the RFC's own and the
 * design's, not invented here.
 */

export const TOTP_STEP_SECONDS = 30;
export const TOTP_DIGITS = 6;
/** Steps accepted either side of the current one. */
export const TOTP_DRIFT_STEPS = 1;

/** 20 random bytes, base32: the length RFC 4226 recommends and every authenticator app accepts. */
export function generateTotpSecret(): string {
  return base32Encode(randomBytes(20));
}

export function totpStep(now: Date): number {
  return Math.floor(now.getTime() / 1000 / TOTP_STEP_SECONDS);
}

export function hotp(secretBase32: string, counter: number): string {
  const key = Buffer.from(base32Decode(secretBase32));
  const msg = Buffer.alloc(8);
  msg.writeBigUInt64BE(BigInt(counter));
  const mac = createHmac("sha1", key).update(msg).digest();
  const offset = (mac[mac.length - 1] ?? 0) & 0x0f;
  const bin =
    (((mac[offset] ?? 0) & 0x7f) << 24) |
    (((mac[offset + 1] ?? 0) & 0xff) << 16) |
    (((mac[offset + 2] ?? 0) & 0xff) << 8) |
    ((mac[offset + 3] ?? 0) & 0xff);
  return String(bin % 10 ** TOTP_DIGITS).padStart(TOTP_DIGITS, "0");
}

export function totpCode(secretBase32: string, now: Date): string {
  return hotp(secretBase32, totpStep(now));
}

export type TotpVerdict =
  | { readonly ok: true; readonly step: number }
  | { readonly ok: false; readonly reason: "malformed" | "wrong_code" | "replay" };

/**
 * A code is good when it equals the code of any step within the drift window
 * AND that step is strictly after `lastUsedStep`. A right code at a used step is
 * `replay`, not `wrong_code`, so the caller can log which. Constant-time compare.
 */
export function verifyTotp(secretBase32: string, typed: string, now: Date, lastUsedStep: number | null): TotpVerdict {
  const code = typed.replace(/ /g, "");
  if (!/^\d{6}$/.test(code)) return { ok: false, reason: "malformed" };
  const current = totpStep(now);
  let matchedStep: number | null = null;
  for (let step = current - TOTP_DRIFT_STEPS; step <= current + TOTP_DRIFT_STEPS; step++) {
    const expected = Buffer.from(hotp(secretBase32, step));
    // No early exit: every window step is compared, so timing does not say which step matched.
    if (timingSafeEqual(expected, Buffer.from(code)) && matchedStep === null) matchedStep = step;
  }
  if (matchedStep === null) return { ok: false, reason: "wrong_code" };
  if (lastUsedStep !== null && matchedStep <= lastUsedStep) return { ok: false, reason: "replay" };
  return { ok: true, step: matchedStep };
}

export function otpauthUri(input: { secret: string; account: string; issuer: string }): string {
  const issuer = encodeURIComponent(input.issuer);
  return (
    `otpauth://totp/${issuer}:${encodeURIComponent(input.account)}` +
    `?secret=${input.secret}&issuer=${issuer}&algorithm=SHA1&digits=${TOTP_DIGITS}&period=${TOTP_STEP_SECONDS}`
  );
}
